import React, { useEffect, useRef, useState } from "react"
import { fetchAnalyticsExport } from "./api"

const extractFilename = (response, fallback) => {
  const disposition = response?.headers?.["content-disposition"]
    || response?.headers?.["Content-Disposition"]
    || ""
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
  return match ? decodeURIComponent(match[1]) : fallback
}

const decodedErrorPayload = data => {
  if (data && typeof data === "object" && !ArrayBuffer.isView(data) && !(data instanceof ArrayBuffer)) return data
  if (typeof data === "string") {
    try { return JSON.parse(data) } catch { return {} }
  }
  if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
    const bytes = data instanceof ArrayBuffer
      ? new Uint8Array(data)
      : new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
    const text = [...bytes].map(byte => String.fromCharCode(byte)).join("")
    try { return JSON.parse(text) } catch { return {} }
  }
  return {}
}

export const exportErrorMessage = error => {
  const status = error?.response?.status
  const message = decodedErrorPayload(error?.response?.data)?.message
  if (status === 409 || message === "ANALYTICS_SNAPSHOT_EXPIRED" || message === "ANALYTICS_SNAPSHOT_MISMATCH") {
    return "This snapshot changed or expired. Refresh analytics, then export again."
  }
  if (status === 401) return "Your session expired. Sign in again before exporting."
  if (status === 403) return "You do not have permission to export analytics."
  if (message === "ANALYTICS_CSV_TABLE_NOT_AVAILABLE") {
    return "That table is no longer available in this view. Reopen Export Current View."
  }
  return "The export could not be generated. Your analytics snapshot is still available."
}

export const triggerDownload = (response, fallback) => {
  const filename = extractFilename(response, fallback)
  const contentType = response?.headers?.["content-type"] || "application/octet-stream"
  const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }))
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
  return filename
}

const ExportCurrentView = ({ query, snapshot, visibleCsvTableKeys, onAccessRequired }) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState("")
  const [error, setError] = useState("")
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const options = snapshot?.exports
  const csvTables = (options?.csvTables || []).filter(option => (
    !visibleCsvTableKeys || visibleCsvTableKeys.includes(option.key)
  ))
  const available = Boolean(snapshot?.metadata?.snapshotId && options?.xlsx)

  useEffect(() => {
    if (open) menuRef.current?.querySelector("button")?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onKeyDown = event => {
      if (event.key === "Escape") {
        event.preventDefault()
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [open])

  if (!available) return null

  const moveMenuFocus = event => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return
    const items = [...(menuRef.current?.querySelectorAll("[role='menuitem']") || [])]
    if (!items.length) return
    event.preventDefault()
    const currentIndex = items.indexOf(document.activeElement)
    if (event.key === "Home") items[0].focus()
    else if (event.key === "End") items[items.length - 1].focus()
    else if (event.key === "ArrowDown") items[(currentIndex + 1 + items.length) % items.length].focus()
    else items[(currentIndex - 1 + items.length) % items.length].focus()
  }

  const download = async ({ format, table, label }) => {
    let accessRevoked = false
    if (loading) return
    setLoading(true)
    setError("")
    setStatus(`Generating ${label} from snapshot ${snapshot.metadata.snapshotId}…`)
    try {
      const response = await fetchAnalyticsExport({
        ...query,
        view: snapshot.view,
        format,
        snapshotId: snapshot.metadata.snapshotId,
        ...(table ? { table } : {}),
      })
      const fallback = `sportya-analytics-${snapshot.view}.${format}`
      const filename = triggerDownload(response, fallback)
      setStatus(`Download ready: ${filename}`)
      setOpen(false)
      triggerRef.current?.focus()
    } catch (requestError) {
      accessRevoked = [401, 403].includes(requestError?.response?.status)
      setStatus("")
      setError(exportErrorMessage(requestError))
      setOpen(false)
      triggerRef.current?.focus()
    } finally {
      setLoading(false)
      if (accessRevoked && onAccessRequired) onAccessRequired()
    }
  }

  return (
    <div className="analytics-export-control">
      <button
        type="button"
        ref={triggerRef}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
        disabled={loading}
      >
        {loading ? "Generating export…" : "Export Current View"}
      </button>
      {open && (
        <div
          className="analytics-export-menu"
          role="menu"
          aria-label="Export Current View formats"
          ref={menuRef}
          onKeyDown={moveMenuFocus}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => download({ format: "xlsx", label: options.xlsx.label })}
          >
            {options.xlsx.label}
          </button>
          {csvTables.map(option => (
            <button
              type="button"
              role="menuitem"
              key={option.key}
              onClick={() => download({ format: "csv", table: option.key, label: option.label })}
            >
              {option.label}
            </button>
          ))}
          {!csvTables.length && (
            <span role="note">No visible data table is available for CSV in this view.</span>
          )}
        </div>
      )}
      {status && <span className="analytics-export-status" role="status" aria-label="Export status">{status}</span>}
      {error && <span className="analytics-export-error" role="alert">{error}</span>}
    </div>
  )
}

export default ExportCurrentView
